// Typed wrappers for the /v1/merchants/:id/payouts/batch endpoint.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
//
// Money handling (CLAUDE.md rule 6): amount_minor is bigint end-to-end. The
// apiClient's transformRequest (json-bigint stringify) serialises bigint as a
// plain JSON integer on the wire; transformResponse (json-bigint parse)
// upgrades integers past Number.MAX_SAFE_INTEGER to bigint. The Raw types
// below use `number | bigint` for fields that come over the wire as a JSON
// integer; the BigInt(...) wraps in getBatch/submitBatch normalise both
// branches to bigint and are no-ops on bigint input.
import { apiClient } from "./client";
import type { CancelBlockedReason } from "./payouts";
import type { Payout } from "./payouts";

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

/**
 * BatchRow — a single payout row in a batch submission.
 * amount_minor is bigint (INR paise) — CLAUDE.md rule 6. Pure BigInt arithmetic only.
 * Goes to the wire as a bigint; apiClient's json-bigint transformer serialises
 * it as a plain JSON integer.
 */
export interface BatchRow {
  beneficiary_id: number;
  /** INR paise as bigint — CLAUDE.md rule 6. Pure BigInt arithmetic only. */
  amount_minor: bigint;
  txn_mode: number;
  request_id: string;
}

/** Payload for POST /v1/merchants/:id/payouts/batch. */
export interface SubmitBatchPayload {
  bank_account_id: number;
  rows: BatchRow[];
}

/**
 * IngestCreateBene — inline auto-register payload on a batch-ingest row.
 * Sent when the CSV had account_no/ifsc/name/email/phone but no existing
 * beneficiary matched. The backend worker registers it with bounded
 * concurrency + retry; the row is held in pending_bene_registration until
 * the worker resolves.
 */
export interface IngestCreateBene {
  name: string;
  account_no: string;
  ifsc: string;
  email: string;
  phone: string;
}

/**
 * IngestBatchRow — same shape as BatchRow but with beneficiary_id OPTIONAL
 * (set when an existing bene matched) and create_bene OPTIONAL (set when
 * a new bene needs registering). Exactly one of the two must be present;
 * the backend rejects rows that have both or neither with
 * code='invalid_row_resolution' and a row_index pointer.
 */
export interface IngestBatchRow {
  /** Set when an existing beneficiary matched. 0/undefined when create_bene is set. */
  beneficiary_id?: number;
  /** Set when a new beneficiary needs registering. Undefined when beneficiary_id is set. */
  create_bene?: IngestCreateBene;
  /** INR paise as bigint — CLAUDE.md rule 6. */
  amount_minor: bigint;
  txn_mode: number;
  request_id: string;
}

/** Payload for POST /v1/merchants/:id/payouts/batch-ingest. */
export interface IngestBatchPayload {
  bank_account_id: number;
  rows: IngestBatchRow[];
}

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/**
 * BatchStatus — possible batch-level statuses from the backend.
 * 'processing' while provider dispatch is running; terminal when all rows
 * have reached success or failure.
 */
export type BatchStatus = "processing" | "complete" | "partial" | "failure";

/**
 * RawBatch — JSON shape of the batch header as returned by axios after
 * json-bigint parse. total_amount_minor is `number | bigint`: a JSON integer
 * that upgrades to bigint only when it would lose precision in f64
 * (lifetime / large-batch totals can exceed 2^53). Normalised to bigint in
 * submitBatch / getBatch.
 */
interface RawBatch {
  id: number;
  merchant_id: number;
  bank_account_id: number;
  submitted_by: number | null;
  row_count: number;
  /** JSON integer (INR paise); bigint past 2^53. */
  total_amount_minor: number | bigint;
  status: BatchStatus;
  created_at: string;
  ingest_state?: BatchIngestState | null;
}

/**
 * BatchIngestState — live progress of the backend's background bene-
 * registration worker. Null on the legacy /batch path (no benes to register).
 * The panel polls the batch detail endpoint while phase === "registering"
 * and stops once phase flips to "done" or "failed".
 */
export interface BatchIngestState {
  phase: "registering" | "done" | "failed";
  total: number;
  done: number;
  failed: number;
  started_at: string;
  finished_at?: string | null;
  error?: string;
}

/**
 * Batch — the typed batch header exposed to UI consumers.
 * total_amount_minor is bigint (INR paise) — CLAUDE.md rule 6. Pure BigInt arithmetic only.
 */
export interface Batch {
  id: number;
  merchant_id: number;
  bank_account_id: number;
  submitted_by: number | null;
  row_count: number;
  /** INR paise as bigint — CLAUDE.md rule 6. Pure BigInt arithmetic only. */
  total_amount_minor: bigint;
  status: BatchStatus;
  created_at: string;
  /** Background-ingestion progress (PDF round 3 N2 follow-up). Null on legacy /batch path. */
  ingest_state?: BatchIngestState | null;
}

/**
 * BatchDetail — the full batch detail response: header + child payouts.
 * Child payouts reuse the Payout shape from payouts.ts (amount_minor: bigint).
 */
export interface BatchDetail {
  batch: Batch;
  payouts: Payout[];
}

/**
 * BatchResponse — the response from POST /v1/merchants/:id/payouts/batch.
 * Returns the newly created batch header (before provider dispatch completes).
 */
export interface BatchResponse {
  batch: Batch;
}

// ---------------------------------------------------------------------------
// Raw response types (deserialization boundary only)
// ---------------------------------------------------------------------------

/**
 * RawPayout — local alias for the JSON shape of a payout child row.
 * Mirrors the RawPayout in payouts.ts; redefined here to keep the module
 * self-contained at the deserialization boundary.
 */
interface RawPayout {
  id: number;
  merchant_id: number;
  /** Nullable parent batch id (post-2c25cbc). */
  batch_id: number | null;
  request_id: string;
  status: "needs_approval" | "pending" | "success" | "failure";
  /** JSON integer (INR paise); bigint past 2^53. */
  amount_minor: number | bigint;
  currency: string;
  txn_mode: number;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  /** Whether this row can be cancelled — the backend's answer, see the Payout
   *  type in payouts.ts. Drives the batch page's "Cancel all (N)" count so the
   *  number matches exactly what the endpoint will act on. */
  can_cancel?: boolean;
  cancel_blocked_reason?: CancelBlockedReason | "";
  export_id?: number | null;
}

/**
 * RawBatchResponse — raw POST response shape before bigint normalisation.
 */
interface RawBatchResponse {
  batch: RawBatch;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * submitBatch — POST /v1/merchants/:merchantId/payouts/batch
 *
 * Submits a validated row set for batch payout dispatch. Requires operator+
 * membership (D-03 RBAC — backend enforces RequireMerchantScope +
 * RequireMembershipRole(operator)).
 *
 * Each row.amount_minor is bigint and crosses the wire untouched — apiClient's
 * json-bigint transformRequest serialises bigint as a plain JSON integer.
 * No Number() coercion (CLAUDE.md rule 6 — pure bigint money path).
 */
export async function submitBatch(
  merchantId: number,
  payload: SubmitBatchPayload,
): Promise<BatchResponse> {
  const resp = await apiClient.post<RawBatchResponse>(
    `/v1/merchants/${merchantId}/payouts/batch`,
    payload,
  );
  return {
    batch: {
      ...resp.data.batch,
      total_amount_minor: BigInt(resp.data.batch.total_amount_minor),
    },
  };
}

/**
 * ingestBatch — POST /v1/merchants/:merchantId/payouts/batch-ingest
 *
 * Server-side background ingestion (PDF round 3 N2 fix). Replaces the
 * panel-side per-row createBeneficiary loop that hit IDFC's rate limit
 * and froze the modal for minutes on large batches. The backend persists
 * the batch + rows in one transaction, returns 202 immediately, and a
 * worker goroutine registers any new beneficiaries with bounded
 * concurrency + retry. Rows with inline create_bene start in
 * status='pending_bene_registration' and transition to 'needs_approval'
 * (success) or 'failure' (retries exhausted) as the worker progresses.
 *
 * The panel typically navigates straight to /payouts/batches/:id after
 * the response — the batch detail page polls ingest_state for live
 * progress.
 */
export async function ingestBatch(
  merchantId: number,
  payload: IngestBatchPayload,
): Promise<BatchResponse> {
  const resp = await apiClient.post<RawBatchResponse>(
    `/v1/merchants/${merchantId}/payouts/batch-ingest`,
    payload,
  );
  return {
    batch: {
      ...resp.data.batch,
      total_amount_minor: BigInt(resp.data.batch.total_amount_minor),
      ingest_state: resp.data.batch.ingest_state ?? null,
    },
  };
}

/**
 * getBatch — GET /v1/merchants/:merchantId/payouts/batches/:batchId
 *
 * Fetches a batch header + its child payout rows. Normalises every amount to
 * bigint at the deserialization boundary (CLAUDE.md rule 6 — money is bigint
 * end-to-end in the SPA). No float coercions.
 */
export async function getBatch(
  merchantId: number,
  batchId: number,
): Promise<BatchDetail> {
  const resp = await apiClient.get<{ batch: RawBatch; payouts: RawPayout[] }>(
    `/v1/merchants/${merchantId}/payouts/batches/${batchId}`,
  );
  return {
    batch: {
      ...resp.data.batch,
      total_amount_minor: BigInt(resp.data.batch.total_amount_minor),
      ingest_state: resp.data.batch.ingest_state ?? null,
    },
    payouts: resp.data.payouts.map((p) => ({
      ...p,
      amount_minor: BigInt(p.amount_minor),
    })),
  };
}

// ─── Bulk approval (migration 019 — Stage 3) ─────────────────────────────────

/** ApproveBatchResult — response shape for POST /admin/payouts/batches/:batchId/approve. */
export interface ApproveBatchResult {
  batch_id: number;
  approved_count: number;
  already_handled_ids: number[];
  dispatch_errors: number;
}

/**
 * approveBatch — POST /v1/admin/payouts/batches/:batchId/approve
 *
 * Bulk-approves every needs_approval row in the batch and dispatches each
 * to the provider. Admin-only at the route layer. Per-row failures don't
 * abort; the response counts surface what happened.
 *
 * For a 500-row batch this can take ~3 minutes (the backend dispatches
 * sequentially) — UI should disable the button + show a spinner while
 * the request is in flight, NOT navigate away.
 */
export async function approveBatch(batchId: number): Promise<ApproveBatchResult> {
  const resp = await apiClient.post<ApproveBatchResult>(
    `/v1/admin/payouts/batches/${batchId}/approve`,
    undefined,
    // Per-call timeout override. Default axios timeout is 15s; the
    // backend dispatches rows sequentially with a provider call each,
    // so a 500-row batch routinely needs ~3 minutes. Without this
    // override the request looks failed in the panel while the
    // backend keeps going and every row actually transitions —
    // ops then see a "failed" toast on top of a queue that empties.
    // 5 minutes covers a ~500-row IDFC batch with margin; nginx
    // proxy_read_timeout is bumped to match.
    { timeout: 300_000 },
  );
  return resp.data;
}

/** RejectBatchResult — response shape for POST /admin/payouts/batches/:batchId/reject. */
export interface RejectBatchResult {
  batch_id: number;
  rejected_count: number;
  already_handled_ids: number[];
}

/**
 * rejectBatch — POST /v1/admin/payouts/batches/:batchId/reject
 *
 * Bulk-rejects every needs_approval row in the batch with the supplied
 * reason. Admin-only. No provider call; the reason persists on each row
 * and lands in audit_log under action='payout.admin_reject'.
 */
export async function rejectBatch(
  batchId: number,
  reason: string,
): Promise<RejectBatchResult> {
  const resp = await apiClient.post<RejectBatchResult>(
    `/v1/admin/payouts/batches/${batchId}/reject`,
    { reason },
    // Per-call timeout override — same reasoning as approveBatch.
    // Reject is cheaper than approve (no provider call) but still
    // runs one DB transaction per row, so a 500-row batch easily
    // beats the 15s axios default. Bumped to 5min for parity.
    { timeout: 300_000 },
  );
  return resp.data;
}
